<template>
    <div>
        <p
            class="mb-0 message-text"
            :class="{
                'pt-1': message.agentName,
                'text-line-clamp': isLongText && !isExpanded,
                'fw-bold': isBold,
                'small': isSmall
            }"
            @click="handleTextClick"
        >
            <template v-for="fragment, i of splitText" :key="`f-${message.id}-${i}`">
                <template v-if="fragment.type == 'text'">
                    <span v-html="fragment.content"></span>
                </template>
                <template v-else-if="fragment.type == 'mention'">
                    <span
                        class="mention-highlight cursor-pointer"
                        @click.stop="chatStore.handleClickOnTextWithAction('phone', (fragment as any).phone)"
                    >{{ fragment.content }}</span>
                </template>
                <template v-else>
                    <TextWithAction :str="fragment.content" @click="chatStore.handleClickOnTextWithAction(fragment.type, fragment.content)" />
                </template>
            </template>
        </p>

        <button
            v-if="isLongText"
            class="btn-read-more"
            @click.stop="toggleReadMore"
        >
            <i :class="isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
            {{ isExpanded ? 'Ler menos' : 'Ler mais' }}
        </button>

    </div>
</template>
<script setup lang="ts">
import { Message } from '@/mocks/app/mockChatData';
import { useChatStore } from '@/stores/app/chatStore';
import { computed } from 'vue';
import { ref } from 'vue';
import { PropType } from 'vue';
import { toast } from 'vue3-toastify';
import TextWithAction from './TextWithAction.vue';
import { escapeHtml } from '@/utils/escapeHtml';

const chatStore = useChatStore();

type BareMinimumMessage = { text: string, uploadError?: boolean, id: string, agentName?: string }

const props = defineProps({
    highlightQuery: { type: String, default: '' },
    message: { type: Object as PropType<BareMinimumMessage|Message>, required: true },
    isBold: { type: Boolean, default: false },
    isSmall: { type: Boolean, default: false },
})

const emit = defineEmits({
    'retry:failed-message'(msg: Message) { return msg },
})

const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// by: José
// RECLAMAR COM CHATGPT OU COM O SISTEMA GLOBAL DE NUMERAÇÃO DE TELEFONES
const splitText = computed((): { type: 'text' | 'phone' | 'mention', content: string, phone?: string }[] => {
const text = props.message.text;
if (!text) return [{ type: 'text', content: '' }];

// --- Helpers ---
const applyFormatting = (s: string): string => {
    if (!s) return '';

    // Search highlight — applied only outside existing HTML tags
    if (props.highlightQuery?.trim()) {
        // O texto já chegou escapado: a busca precisa casar com a mesma forma.
        const safeQuery = escapeHtml(props.highlightQuery).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        s = s.replace(new RegExp(`(?![^<]*>)(${safeQuery})`, 'gi'),
            '<span class="search-highlight">$1</span>');
    }

    // Links — trailing punctuation excluded from href
    s = s.replace(
        /(https?:\/\/[^\s<]+?)([.,!?)]*(?=\s|$))/g,
        (_, url, trail) => `<a href="${url}" target="_blank" class="chat-link">${url}</a>${trail}`
    );

    // Markdown
    s = s.replace(/\*([^*\n]+)\*/g, '<b>$1</b>');
    s = s.replace(/(^|\s)_([^_\n]+?)_(?=\s|[.,!?]|$)/gm, '$1<i>$2</i>');
    s = s.replace(/~([^~\n]+)~/g, '<s>$1</s>');
    s = s.replace(/```([^`]+)```/g, '<code class="chat-code">$1</code>');

    return s.replace(/\n/g, '<br />');
};

const resolveContact = (digits: string) => {
    const contato =
        chatStore.contatosByCodigo.get(digits)
        ?? chatStore.contatosByCodigo.get(digits.replace(/^55(\d{2})9(\d{8})$/, '55$1$2'))
        ?? chatStore.contatosByCodigo.get(digits.replace(/^55(\d{2})(\d{8})$/, '55$19$2'));
    if (contato) return contato;

    const participants = chatStore.activeChat?.participants ?? [];
    const participant =
        participants.find(p => p.phone.replace(/\D/g, '') === digits)
        ?? participants.find(p => p.phone.replace(/\D/g, '') === digits.replace(/^55(\d{2})9(\d{8})$/, '55$1$2'))
        ?? participants.find(p => p.phone.replace(/\D/g, '') === digits.replace(/^55(\d{2})(\d{8})$/, '55$19$2'));
    if (participant) return { nome: participant.name };

    return undefined;
};

    // --- Tokenise: @mentions and bare phone numbers ---
    // Mentions first (@ + digits), then bare phone candidates
    const tokenRegex = /@[\d+\s()+-]{10,20}|[\d()+-][\d\s()+-]{8,18}[\d]/g;

    // Mask URLs with same-length spaces so the tokenizer never matches digits
    // inside a URL nor bridges a URL boundary into an adjacent phone number.
    let maskedText = text;
    for (const m of text.matchAll(/https?:\/\/[^\s<]+/g)) {
        maskedText = maskedText.slice(0, m.index!) + ' '.repeat(m[0].length) + maskedText.slice(m.index! + m[0].length);
    }

    const result: { type: 'text' | 'phone' | 'mention', content: string, phone?: string }[] = [];
    let last = 0;

    for (const match of maskedText.matchAll(tokenRegex)) {
        // Always read content from the original text (same indices since masking preserves length)
        const raw = text.slice(match.index!, match.index! + match[0].length).replace(/\s+$/, '');
        const isMention = raw.startsWith('@');
        const digits = raw.replace(/\D/g, '');

        // Validate digit count (10–15 for phones)
        if (digits.length < 10 || digits.length > 15) continue;

        const start = match.index!;
        const end = start + raw.length;

        // Push any plain text before this token
        const before = text.slice(last, start);
        if (before) result.push({ type: 'text', content: applyFormatting(escapeHtml(before)) });

        if (isMention) {
            const contato = resolveContact(digits);
            const displayName = contato?.nome ? `@${contato.nome}` : `@${digits}`;
            result.push({ type: 'mention', content: displayName, phone: digits });
        } else {
            result.push({ type: 'phone', content: raw, phone: digits });
        }

        last = end;
    }

    // Remaining text after last token
    const tail = text.slice(last);
    if (tail) result.push({ type: 'text', content: applyFormatting(escapeHtml(tail)) });

    return result.length ? result : [{ type: 'text', content: '' }];
});

const isExpanded = ref(false);

function toggleReadMore() {
    isExpanded.value = !isExpanded.value;
}

const isLongText = computed(() => {
    const text = props.message.text || '';
    // Considera longo se tiver mais de 500 caracteres OU mais de 12 quebras de linha
    const hasManyChars = text.length > 500;
    const hasManyLines = (text.match(/\n/g) || []).length > 12;

    return hasManyChars || hasManyLines;
});

function handleTextClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // Verifica se clicou especificamente na menção
    if (target.classList.contains('mention-highlight')) {
        const phone = target.getAttribute('data-phone');

        if (phone) {
            openNewChat(phone);
        } else {
            console.warn('Participante mencionado sem telefone vinculado.');
        }
    }
}

// Simulação de abertura de conversa
function openNewChat(phone?: string): void {
    if (!phone) return;
    const foundChat = chatStore.chatList.find(c => c.codigoContato == phone);
    const foundChatId = foundChat?.id;
    if (!foundChatId) 
        return void toast.info('Nenhum chat com o participante mencionado.')
    else 
        return void chatStore.selectChat(foundChatId)
}
</script>
<style scoped>

</style>